import React, { useState, createContext, useContext, useEffect, useRef } from "react";
import useIdleTimer from "./hooks/useIdleTimer";
import { useRoleContext } from "./hooks/useRoleContext";
import { PermissionsProvider, usePermissions } from "./context/PermissionsContext";
import SessionLockOverlay from "./components/SessionLockOverlay";
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { subscribeToPush } from "./utils/push";
import { useFcmPush, unregisterFcmToken } from "./utils/useFcmPush";
import API from "./utils/api";
import NotificationBell from "./components/NotificationBell";
import EmployeeExperience from "./components/EmployeeExperience";
import CelebrationHub from "./components/CelebrationHub";
import InteractionPolish from "./components/InteractionPolish";
import ExpenseSyncStatus from "./components/ExpenseSyncStatus";
import EmployeeDashboard from "./pages/employee/Dashboard";
import EmployeeTrips from "./pages/employee/Trips";
import EmployeeTripDetail from "./pages/employee/TripDetail";
import ManagerDashboard from "./pages/manager/Dashboard";
import ManagerAnalytics from "./pages/manager/ManagerAnalytics";
import ManagerExpenseReport from "./pages/manager/ManagerExpenseReport";
import ManagerTrips from "./pages/manager/ManagerTrips";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminAdvances from "./pages/admin/Advances";
import AdminReports from "./pages/admin/Reports";
import AdminIssueAdvance from "./pages/admin/IssueAdvance";
import AdminAnalytics from "./pages/admin/Analytics";
import ExecutiveDashboard from "./pages/executive/ExecutiveDashboard";
import Approvals from "./pages/approvals/Approvals";
import AuditDashboard from "./pages/audit/AuditDashboard";
import SemanticAuditTrail from "./pages/audit/SemanticAuditTrail";
import MyReimbursements from "./pages/employee/MyReimbursements";
import QueryInbox from "./pages/shared/QueryInbox";
import PolicyRules from "./pages/admin/PolicyRules";
import MyAttendance from "./pages/employee/MyAttendance";
import ChairmanReview from "./pages/executive/ChairmanReview";
import OETExamCosts from "./pages/admin/OETExamCosts";
import TestDataCleanup from "./pages/admin/TestDataCleanup";
import { redirectWithSsoCode } from "./utils/ssoRedirect";

const UNIFIED_LOGIN_URL = process.env.REACT_APP_HRMS_LOGIN_URL || "http://localhost:5175/login";
const defaultSsoUrl = `${window.location.protocol}//${window.location.hostname}:7000`;
const SSO_API_URL = process.env.REACT_APP_SSO_URL || defaultSsoUrl;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  });

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    const keepChat = localStorage.getItem("company_chat_keep_signed_in") === "1";
    const chatToken = localStorage.getItem("company_chat_token");
    localStorage.clear();
    if (keepChat) {
      localStorage.setItem("company_chat_keep_signed_in", "1");
      if (chatToken) localStorage.setItem("company_chat_token", chatToken);
    }
    setUser(null);
    window.location.href = UNIFIED_LOGIN_URL;
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

const getExpenseRole = (user) => user?.app_role_overrides?.expense || user?.role;

function PrivateRoute({ children, roles, superadminOnly = false }) {
  const { user } = useAuth();
  if (!user) return <UnifiedLoginRedirect />;
  const role = getExpenseRole(user);
  // superadminOnly routes: only superadmin can access (blocks restricted admins)
  if (superadminOnly) {
    if (role !== "superadmin") return <Navigate to="/admin" replace />;
    return children;
  }
  // Superadmin and executive bypass all role restrictions
  if (role === "superadmin" || role === "executive") return children;
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />;
  return children;
}

// Reads the live auth context — avoids the stale module-level `role` variable
// that was evaluated before SSO set localStorage on first load.
function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <UnifiedLoginRedirect />;
  const role = getExpenseRole(user);
  // superadmin/manager/team_leader start on the Self dashboard
  // (they can switch to My Team / Super Admin via the context pills)
  return <Navigate to={
    role === "executive" ? "/executive" : "/dashboard"
  } replace />;
}

function SsoLanding() {
  const { login } = useAuth();
  const location = useLocation();
  const didExchange = useRef(false);

  useEffect(() => {
    // React 18 Strict Mode double-invokes effects in development. SSO codes are single-use:
    // the second concurrent exchange request would always get 401 and redirect to HRMS.
    // This ref guard ensures only the first invocation proceeds.
    if (didExchange.current) return;
    didExchange.current = true;
    const params = new URLSearchParams(location.search);
    const ssoCode = params.get("sso_code");
    const ssoToken = params.get("sso_token");     // from SSO service (:7000)
    const legacyToken = params.get("token");       // from HRMS AppLauncher
    const legacyUser  = params.get("user");        // from HRMS AppLauncher

    async function completeSignIn() {
      let loggedInUser = null;

      const exchangeCode = async (code) => {
        const response = await fetch(`${SSO_API_URL.replace(/\/$/, "")}/sso/exchange`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || "SSO exchange failed");
        return payload;
      };

      const hrmsPassthrough = params.get("hrms_token");
      if (ssoCode) {
        let data;
        try {
          data = await exchangeCode(ssoCode);
        } catch (firstError) {
          const recovery = await fetch(`${SSO_API_URL.replace(/\/$/, "")}/sso/trusted/code`, {
            method: "POST", credentials: "include",
          });
          const recoveryData = await recovery.json();
          if (!recovery.ok) throw firstError;
          data = await exchangeCode(recoveryData.sso_code);
        }
        loggedInUser = data.user;
        login(data.token, loggedInUser);
        localStorage.setItem("company_chat_token", data.token);
        localStorage.setItem("hrms_token", data.token);
      } else if (ssoToken && process.env.NODE_ENV !== "production") {
        const base64 = ssoToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        loggedInUser = JSON.parse(atob(base64));
        login(ssoToken, loggedInUser);
        localStorage.setItem("company_chat_token", ssoToken);
        localStorage.setItem("hrms_token", hrmsPassthrough || ssoToken);
      } else if (legacyToken && legacyUser) {
        // HRMS AppLauncher flow — user object already provided as JSON in URL
        loggedInUser = JSON.parse(decodeURIComponent(legacyUser));
        login(legacyToken, loggedInUser);
        localStorage.setItem("company_chat_token", legacyToken);
        localStorage.setItem("hrms_token", hrmsPassthrough || legacyToken);
      } else {
        window.location.replace(UNIFIED_LOGIN_URL);
        return;
      }

      // Use window.location.replace (full page reload) instead of navigate().
      // login() writes localStorage synchronously BEFORE this redirect, so when
      // AuthProvider re-initialises on the new page load it reads the user from
      // localStorage immediately — no React state timing race with PrivateRoute.
      const role = loggedInUser?.role;
      // Honour a ?next= deep-link param (e.g. /admin/analytics, /audit-trail)
      // sent by the HRMS executive dashboard open-app helper.
      const nextParam = params.get("next");

      // Always land on "self" (employee self-view) on login/app-switch for
      // manager/team_leader AND superadmin. They can switch to My Team /
      // Super Admin via the context pills. Executive keeps its own view.
      const defaultDest = role === "executive" ? "/executive" : "/dashboard";
      const dest = nextParam || defaultDest;

      // Clear any persisted context so every pill-role starts fresh on Self.
      const email = loggedInUser?.email ?? "";
      const ctxKey = `role_ctx_expense_${email}`;
      if (role === "executive") {
        localStorage.setItem(ctxKey, "exec");
      } else {
        localStorage.removeItem(ctxKey);
      }

      window.location.replace(window.location.origin + "/#" + dest);
    }

    completeSignIn().catch(() => {
      window.location.replace(UNIFIED_LOGIN_URL);
    });
  }, [location.search, login]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "Inter, sans-serif", color: "#374151" }}>
      Opening Expense Management...
    </div>
  );
}

function UnifiedLoginRedirect() {
  useEffect(() => {
    window.location.replace(UNIFIED_LOGIN_URL);
  }, []);
  return null;
}

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await API.post("/auth/login", { email: email.trim().toLowerCase(), password });
      login(res.data.token, res.data.user);
      const role = res.data.user.role;
      navigate(
        role === "executive"  ? "/executive" :
        role === "superadmin" ? "/admin"     :
        role === "manager"    ? "/manager"   :
        "/dashboard",
        { replace: true }
      );
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      fontFamily: "Inter, system-ui, sans-serif",
      padding: 16,
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
        padding: "40px 36px",
        width: "100%",
        maxWidth: 420,
      }}>
        {/* Logo / brand */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#E31E24", letterSpacing: -1, lineHeight: 1 }}>
            ebek<span style={{ fontSize: "65%" }}>★</span>
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
            Expense Portal
          </div>
          <div style={{ height: 2, background: "#E31E24", borderRadius: 2, width: 36, margin: "0 auto" }} />
        </div>

        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: "0 0 6px", textAlign: "center" }}>
          Sign in to your account
        </h2>
        <p style={{ fontSize: 13, color: "#6b7280", textAlign: "center", margin: "0 0 24px" }}>
          Use your Ebek email and password
        </p>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fca5a5",
            borderRadius: 8, padding: "10px 14px",
            color: "#dc2626", fontSize: 13, fontWeight: 600,
            marginBottom: 18,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="you@ebekindia.com"
              style={{
                width: "100%", boxSizing: "border-box",
                height: 42, padding: "0 12px",
                border: "1.5px solid #e5e7eb",
                borderRadius: 8, fontSize: 14,
                fontFamily: "inherit", outline: "none",
                transition: "border-color 0.15s",
              }}
              onFocus={e => e.target.style.borderColor = "#E31E24"}
              onBlur={e => e.target.style.borderColor = "#e5e7eb"}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                style={{
                  width: "100%", boxSizing: "border-box",
                  height: 42, padding: "0 40px 0 12px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: 8, fontSize: 14,
                  fontFamily: "inherit", outline: "none",
                  transition: "border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor = "#E31E24"}
                onBlur={e => e.target.style.borderColor = "#e5e7eb"}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#9ca3af", padding: 4,
                }}
                tabIndex={-1}
              >
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%", height: 44,
              background: loading ? "#f87171" : "#E31E24",
              color: "#fff", border: "none",
              borderRadius: 8, fontSize: 15,
              fontWeight: 800, cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              transition: "background 0.15s, transform 0.1s",
            }}
            onMouseEnter={e => { if (!loading) e.target.style.background = "#b91c1c"; }}
            onMouseLeave={e => { if (!loading) e.target.style.background = "#E31E24"; }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <a
            href={UNIFIED_LOGIN_URL}
            style={{ fontSize: 12, color: "#6b7280", textDecoration: "none" }}
          >
            Sign in via HRMS →
          </a>
        </div>
      </div>
    </div>
  );
}

function AppSwitcher() {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(null);
  const CRM_URL          = process.env.REACT_APP_CRM_URL   || "http://localhost:4001";
  const CRM_API_URL      = process.env.REACT_APP_CRM_API_URL || "http://localhost:4001/api";
  // HRMS base URL — navigate here and HomeRedirect routes each role to the right dashboard.
  // We do NOT navigate to /apps (workspace picker) — that's only for first-time login.
  const HRMS_BASE_URL    = process.env.REACT_APP_HRMS_URL   || "http://localhost:5000";

  // Get allowedApps to filter the app switcher buttons (same logic as CRM sidebar)
  const { allowedApps } = useRoleContext(user, "expense");

  const ALL_APPS = [
    { key: "hrms",    label: "HRMS",    icon: "👤" },
    { key: "crm",     label: "CRM",     icon: "📊" },
    { key: "expense", label: "Expense", icon: "💰" },
  ];
  const apps = ALL_APPS.filter(a => allowedApps.includes(a.key));

  const goTo = async (appKey) => {
    if (appKey === "expense") return;
    if (appKey === "hrms") {
      // The user already has hrms_token in localStorage from the original HRMS login.
      // Navigate to HRMS root — HomeRedirect will send each role to the right dashboard
      // (/dashboard for employees & managers, /executive-dashboard for executives).
      // We never go to /apps here — that page is the workspace picker, not the dashboard.
      window.location.href = HRMS_BASE_URL;
      return;
    }
    const hrmsToken = localStorage.getItem("hrms_token");
    const t = hrmsToken || localStorage.getItem("expense_token") || localStorage.getItem("company_chat_token");
    if (!t) return;
    setBusy(appKey);
    try {
      if (appKey === "crm") {
        await redirectWithSsoCode({ targetUrl: CRM_URL, token: hrmsToken || t });
      }
    } catch { setBusy(null); }
  };

  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  return (
    <div style={{ padding: "10px 16px 12px", borderTop: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
        Switch App
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {apps.map(app => {
          const isCur = app.key === "expense";
          return (
            <button key={app.key}
              onClick={() => goTo(app.key)}
              disabled={busy === app.key}
              title={isCur ? "Current App" : `Go to ${app.label}`}
              style={{
                flex: 1, padding: "5px 4px", border: "1.5px solid",
                borderColor: isCur ? accent : "#e2e8f0",
                borderRadius: 8, background: isCur ? `${accent}18` : "#fff",
                color: isCur ? accent : "#64748b",
                fontSize: 10, fontWeight: 800, fontFamily: "inherit",
                cursor: isCur ? "default" : "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              }}>
              <span style={{ fontSize: 15 }}>{busy === app.key ? "…" : app.icon}</span>
              {app.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


const COMPANY_CONFIG = {
  ebek:    { accent: "#E31E24", label: "Ebek",         logo: "/logo-ebek-crm.png"    },
  ebekmed: { accent: "#2AB5A5", label: "Ebek Med",      logo: "/logo-ebekmed-skills.png" },
  all:     { accent: "#7C3AED", label: "All Companies", logo: null                },
};

function SidebarLogo({ company }) {
  const [imgFailed, setImgFailed] = useState(false);
  const cfg = COMPANY_CONFIG[company] || COMPANY_CONFIG.ebek;

  if (company === "all") {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 28 }}>🔐</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#7C3AED" }}>Finance Admin</div>
        <div style={{ fontSize: 9, color: "#9ca3af" }}>All Companies</div>
      </div>
    );
  }

  if (!imgFailed && cfg.logo) {
    return (
      <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "flex-start", padding: "0 4px", overflow: "hidden" }}>
        <img
          src={cfg.logo}
          alt={cfg.label}
          className={`expense-brand-logo ${company === "ebekmed" ? "" : "is-ebek-logo"}`}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  if (company === "ebekmed") {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#2AB5A5" }}>
          ebek <span style={{ color: "#E31E24" }}>med</span>
        </div>
        <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: 1 }}>Your Skills Partner</div>
      </div>
    );
  }
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 26, fontWeight: 900, color: "#E31E24", letterSpacing: -1 }}>
        ebek<span style={{ fontSize: "65%" }}>★</span>
      </div>
      <div style={{ fontSize: 9, color: "#9ca3af", letterSpacing: 1, textTransform: "uppercase" }}>
        Communication Empowered
      </div>
    </div>
  );
}

// Maps Expense nav paths → permission module keys (null = always show)
// Keys MUST match what AccessControl.jsx stores in role_permissions.module_key
const EXPENSE_NAV_MODULE_MAP = {
  "/dashboard":              null,                // always visible
  "/trips":                  null,                // always visible — self-service for all roles
  "/my-reimbursements":      null,                // always visible — self-service for all roles
  "/my-attendance":          null,                // always visible
  "/queries":                null,                // always visible
  "/manager":                null,                // always visible (role-gated, not perm-gated)
  "/manager/trips":          null,                // always visible for manager/team_leader
  "/approvals":              "expense_approvals", // AccessControl key: expense_approvals
  "/audit":                  null,                // always visible to managers (no separate perm key)
  "/manager/analytics":      "manager_analytics", // AccessControl key: manager_analytics
  "/manager/expense-report": "expense_report",    // AccessControl key: expense_report
  "/admin":                  null,                // always visible (role-gated, not perm-gated)
  "/audit-trail":            "audit_log",         // AccessControl key: audit_log ✓
  "/admin/advances":         "admin_advances",    // AccessControl key: admin_advances
  "/admin/policy":           "policy_rules",      // AccessControl key: policy_rules
  "/admin/oet-costs":        "oet_costs",         // AccessControl key: oet_costs ✓
  "/admin/analytics":        "manager_analytics", // same module, different context
  "/admin/issue-advance":    null,                // superadmin only — no separate perm key
  "/admin/reports":          "admin_reports",     // AccessControl key: admin_reports
};

function Layout({ children }) {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [auditOpenCount, setAuditOpenCount] = useState(0);
  const [queryBadge, setQueryBadge] = useState(0);

  // Register VAPID push subscription (legacy, still works)
  useEffect(() => {
    if (user) subscribeToPush().catch(() => {});
  }, [user?.id]); // re-run if user changes (different login)

  // Register FCM push token (Phase 2 — works when Firebase env vars are set)
  useFcmPush(user);

  const expenseRole = getExpenseRole(user);

  // Fetch open audit flag count for badge (manager/team_leader/superadmin)
  useEffect(() => {
    const role = getExpenseRole(user);
    if (!["manager","team_leader","superadmin"].includes(role)) return;
    const fetchAudit = () => {
      API.get("/audit/flags/summary")
        .then(r => setAuditOpenCount(r.data?.open?.total || 0))
        .catch(() => {});
    };
    fetchAudit();
    const interval = setInterval(fetchAudit, 120000);
    return () => clearInterval(interval);
  }, [user?.role, user?.app_role_overrides?.expense]); // eslint-disable-line

  // Fetch query badge count for ALL roles
  useEffect(() => {
    if (!user) return;
    const fetchQueryBadge = () => {
      API.get("/queries/summary")
        .then(r => setQueryBadge(r.data?.count || 0))
        .catch(() => {});
    };
    fetchQueryBadge();
    const interval = setInterval(fetchQueryBadge, 60000);
    return () => clearInterval(interval);
  }, [user?.id]); // eslint-disable-line

  // Fetch pending approvals count for badge
  useEffect(() => {
    const role = getExpenseRole(user);
    if (!["manager","team_leader","superadmin"].includes(role)) return;
    const fetchCount = () => {
      const reqs = [];
      if (role === "manager" || role === "team_leader") {
        reqs.push(
          API.get("/advance-requests/pending").then(r => r.data?.length || 0).catch(() => 0)
        );
        reqs.push(
          API.get("/expenses/bf-override-requests/pending").then(r => r.data?.length || 0).catch(() => 0)
        );
      }
      reqs.push(
        API.get(`/expenses/cash-bill-approvals/pending${role === "superadmin" ? "?view=all" : ""}`)
          .then(r => (r.data || []).filter(item => item.canApprove !== false).length)
          .catch(() => 0)
      );
      Promise.all(reqs).then(counts => setPendingCount(counts.reduce((a, b) => a + b, 0)));
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [user?.role, user?.app_role_overrides?.expense]); // eslint-disable-line

  const handleLogout = async () => { await unregisterFcmToken(); logout(); };
  const isActive = (path) => location.pathname.startsWith(path) ? "nav-item active" : "nav-item";
  const co = COMPANY_CONFIG[user?.company] || COMPANY_CONFIG.ebek;

  const employeeNav = [
    { path: "/dashboard",         icon: "🏠", label: "Dashboard"       },
    { path: "/trips",             icon: "✈️", label: "My Trips"        },
    { path: "/my-reimbursements", icon: "💰", label: "Reimbursements"  },
    { path: "/queries",           icon: "🔔", label: "Queries",         badge: queryBadge },
  ];
  const managerNav = [
    { path: "/manager",                  icon: "🏠", label: "Dashboard"       },
    { path: "/manager/trips",            icon: "✈️", label: "Team Trips"      },
    { path: "/my-reimbursements",        icon: "💰", label: "Reimbursements"  },
    { path: "/approvals",                icon: "✅", label: "Approvals",       badge: pendingCount },
    { path: "/audit",                    icon: "🔍", label: "Audit Review",    badge: auditOpenCount },
    { path: "/queries",                  icon: "🔔", label: "Queries",          badge: queryBadge },
    { path: "/manager/analytics",        icon: "📊", label: "Analytics"       },
    { path: "/manager/expense-report",   icon: "📋", label: "Monthly Review Submission" },
    { path: "/admin/advances",           icon: "💰", label: "Returns & Settlements" },
  ];
  // Admin nav is superadmin-only (admin role has been removed from the system)
  const adminNav = [
    { path: "/admin",                icon: "🏠", label: "Dashboard"        },
    { path: "/approvals",            icon: "✅", label: "Approvals",        badge: pendingCount },
    { path: "/audit",                icon: "🔍", label: "Audit Review",     badge: auditOpenCount },
    { path: "/audit-trail",          icon: "📋", label: "Audit Trail"       },
    { path: "/queries",              icon: "🔔", label: "Queries",           badge: queryBadge },
    { path: "/admin/advances",       icon: "💰", label: "Advances & Settle" },
    { path: "/admin/policy",         icon: "📋", label: "Policy Rules"      },
    { path: "/admin/oet-costs",      icon: "🏫", label: "OET Costing"       },
    { path: "/admin/analytics",      icon: "📈", label: "Analytics"         },
    { path: "/admin/issue-advance",  icon: "💸", label: "Issue Advance"    },
    { path: "/admin/reports",        icon: "📊", label: "Reports"           },
    { path: "/admin/test-data",      icon: "🧹", label: "Testing Data Cleanup" },
  ];

  const executiveNav = [
    { path: "/executive",                    icon: "📊", label: "Executive Overview" },
    { path: "/executive/chairman-review",    icon: "👑", label: "Chairman Review"    },
    { path: "/admin/analytics",              icon: "📈", label: "Analytics"          },
    { path: "/admin/reports",               icon: "📋", label: "Reports"            },
  ];

  // ── Context-switcher integration ─────────────────────────────────────────
  const { context, setContext, pillDef, allowedApps } = useRoleContext(user, "expense");

  const isExecutive = expenseRole === "executive";
  const isAdmin     = expenseRole === "superadmin"; // "admin" role removed — only superadmin has admin access

  // Choose nav array based on active context pill.
  // Additionally, if the URL is already in the admin/manager section, honour that
  // even if context hasn't been persisted yet (e.g. first SSO login on a new device).
  // Context pill is the single source of truth for nav — URL path is not used
  // (removing isOnAdminPath / isOnManagerPath overrides that were causing pill to have no effect)
  let navItems;
  if (isExecutive) {
    navItems = executiveNav;
  } else if (context === "admin" && isAdmin) {
    navItems = adminNav;
  } else if (context === "team" && (isAdmin || ["manager", "team_leader"].includes(expenseRole))) {
    navItems = managerNav;
  } else {
    navItems = employeeNav;
  }

  // Apply module-level permissions filter (permissive fallback — never hides items if API unreachable)
  navItems = navItems.filter(item => {
    const modKey = EXPENSE_NAV_MODULE_MAP[item.path];
    if (modKey === undefined || modKey === null) return true;
    return can(modKey);
  });

  const APP_URLS = {
    hrms:    process.env.REACT_APP_HRMS_URL    || "http://localhost:5000",
    crm:     process.env.REACT_APP_CRM_URL     || "http://localhost:4001",
    expense: window.location.origin,
  };
  const APP_META = [
    { key: "hrms",    icon: "👤", label: "HRMS"    },
    { key: "crm",     icon: "📊", label: "CRM"     },
    { key: "expense", icon: "💰", label: "Expense" },
  ];

  const roleFallback = {
    superadmin: "Super Admin",
    executive: "Executive",
    manager: "Manager",
    team_leader: "Team Leader",
    admin: "Admin",
    employee: "Employee",
  }[user?.role] || user?.role;
  const displayDesignation = user?.designation || roleFallback;

  return (
    <div style={{ display: "flex" }}>
      {menuOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMenuOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 99,
            display: "none",
          }}
        />
      )}
      <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
        <div className="sidebar-logo">
          <SidebarLogo company={user?.company} />
        </div>

        {/* ── Context pill switcher (Self / My Team / Admin) ── */}
        {pillDef.labels.length > 0 && (
          <div style={{ padding: "12px 16px 8px", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              Viewing as
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {pillDef.labels.map((label, i) => {
                const ctx = pillDef.ctxs[i];
                const active = context === ctx;
                const accent = (user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24");
                return (
                  <button key={ctx} onClick={() => setContext(ctx)} style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1.5px solid",
                    borderColor: active ? "transparent" : "#e2e8f0",
                    borderRadius: 16,
                    background: active ? `linear-gradient(135deg, ${accent}, ${accent}cc)` : "#f8fafc",
                    color: active ? "#fff" : "#64748b",
                    fontSize: 11,
                    fontWeight: 800,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    boxShadow: active ? `0 2px 8px ${accent}44` : "none",
                    transition: "all 0.15s",
                  }}>{label}</button>
                );
              })}
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          <div style={{ padding: "8px 20px 4px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Menu
          </div>
          {navItems.map(item => (
            <Link key={item.path} to={item.path} className={isActive(item.path)} onClick={() => setMenuOpen(false)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                {item.label}
              </span>
              {item.badge > 0 && (
                <span style={{
                  background: "#DC2626", color: "white", borderRadius: 20,
                  padding: "1px 8px", fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: "center"
                }}>
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>


        <AppSwitcher />
        <button type="button" className="expense-version-button"
          onClick={() => window.dispatchEvent(new Event("ebek:about-open"))}
          aria-label="Open Ebek One version and what's new">
          <span className="expense-version-dot" />
          <span>Ebek One</span>
          <span>v2026.08</span>
        </button>
        <div style={{ padding: "16px 20px", borderTop: "1px solid #f3f4f6" }}>
          <button className="nav-item" onClick={handleLogout} style={{ color: "#DC2626", width: "100%" }}>
            Logout
          </button>
        </div>
      </aside>

      <div className="main-content" style={{ flex: 1 }}>
        <header className="topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => setMenuOpen(!menuOpen)}
              style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#111", display: "none" }}
              className="mobile-menu-btn">☰</button>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>{co.label} — Expense Portal</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Expense Management</div>
            </div>
          </div>
          <div className="topbar-profile no-ripple">
            <NotificationBell />
            <div className="topbar-user-text" style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>{user?.name}</div>
              <div style={{ fontSize: 12, color: "#334155", fontWeight: 600, textTransform: "capitalize" }}>{displayDesignation} · {co.label}</div>
            </div>
            <div className="topbar-profile-avatar" style={{ background: `linear-gradient(135deg, ${co.accent}, ${co.accent}99)` }}>
              {user?.name?.[0]?.toUpperCase()}
              {user?.profile_photo && <img src={user.profile_photo} alt="" />}
            </div>
          </div>
        </header>
        <main className="page-content">{children}</main>
      </div>
    </div>
  );
}




// ── Session idle timeout ─────────────────────────────────────────
function SessionGuard({ children }) {
  const { user, logout } = useAuth();
  const [locked, setLocked] = useState(false);

  const handleIdle   = () => { if (user) setLocked(true);  };
  const handleLogout = () => { setLocked(false); logout(); };

  const { reset } = useIdleTimer({
    onIdle:   handleIdle,
    onLogout: handleLogout,
    idleMs:   15 * 60 * 1000,
    logoutMs: 30 * 60 * 1000,
    enabled:  !!user,
  });

  const onUnlock = () => { setLocked(false); reset(); };

  return (
    <>
      {children}
      {locked && user && (
        <SessionLockOverlay user={user} onUnlock={onUnlock} onLogout={handleLogout} />
      )}
    </>
  );
}

function AppInner() { return null; }

// Reads the live user from AuthContext and provides module-level Expense permissions
function PermissionsInit({ children }) {
  const { user } = useAuth();
  return <PermissionsProvider user={user}>{children}</PermissionsProvider>;
}

function CelebrationInit() { const { user } = useAuth(); return user ? <><CelebrationHub company={user.company} /><InteractionPolish company={user.company} /></> : null; }

export default function App() {
  return (
    <AuthProvider>
      <PermissionsInit>
      <SessionGuard>
      <AppInner />
      <HashRouter>
        <EmployeeExperience />
        <CelebrationInit />
        <ExpenseSyncStatus />
        <Toaster position="top-right" toastOptions={{ style: { fontFamily: "Inter, sans-serif", fontSize: 14 } }} />
        <Routes>
          <Route path="/sso" element={<SsoLanding />} />
          <Route path="/login" element={<UnifiedLoginRedirect />} />
          <Route path="/" element={<RootRedirect />} />

          {/* Employee */}
          <Route path="/dashboard"         element={<PrivateRoute><Layout><EmployeeDashboard /></Layout></PrivateRoute>} />
          <Route path="/trips"             element={<PrivateRoute><Layout><EmployeeTrips /></Layout></PrivateRoute>} />
          <Route path="/trips/:id"         element={<PrivateRoute><Layout><EmployeeTripDetail /></Layout></PrivateRoute>} />
          <Route path="/my-reimbursements" element={<PrivateRoute><Layout><MyReimbursements /></Layout></PrivateRoute>} />
          <Route path="/my-attendance"     element={<PrivateRoute><Layout><MyAttendance /></Layout></PrivateRoute>} />

          {/* Queries — all roles */}
          <Route path="/queries" element={<PrivateRoute><Layout><QueryInbox /></Layout></PrivateRoute>} />

          {/* Manager / Team Leader */}
          <Route path="/manager"                element={<PrivateRoute roles={["manager","team_leader"]}><Layout><ManagerDashboard /></Layout></PrivateRoute>} />
          <Route path="/manager/analytics"      element={<PrivateRoute roles={["manager","team_leader"]}><Layout><ManagerAnalytics /></Layout></PrivateRoute>} />
          <Route path="/manager/expense-report" element={<PrivateRoute roles={["manager","team_leader"]}><Layout><ManagerExpenseReport /></Layout></PrivateRoute>} />
          <Route path="/manager/trips"          element={<PrivateRoute roles={["manager","team_leader"]}><Layout><ManagerTrips /></Layout></PrivateRoute>} />

          {/* Approvals — managers, team_leaders, superadmins */}
          <Route path="/approvals" element={<PrivateRoute roles={["manager","team_leader","superadmin"]}><Layout><Approvals /></Layout></PrivateRoute>} />

          {/* Audit — managers, team_leaders, superadmins */}
          <Route path="/audit" element={<PrivateRoute roles={["manager","team_leader","superadmin"]}><Layout><AuditDashboard /></Layout></PrivateRoute>} />

          {/* Semantic Audit Trail — superadmin/executive */}
          <Route path="/audit-trail" element={<PrivateRoute roles={["superadmin","executive"]}><Layout><SemanticAuditTrail /></Layout></PrivateRoute>} />

          {/* Superadmin only */}
          <Route path="/admin"               element={<PrivateRoute roles={["superadmin"]}><Layout><AdminDashboard /></Layout></PrivateRoute>} />
          <Route path="/admin/advances"      element={<PrivateRoute roles={["manager"]}><Layout><AdminAdvances /></Layout></PrivateRoute>} />
          <Route path="/admin/issue-advance" element={<PrivateRoute roles={["superadmin"]}><Layout><AdminIssueAdvance /></Layout></PrivateRoute>} />
          <Route path="/admin/reports"       element={<PrivateRoute roles={["superadmin","executive"]}><Layout><AdminReports /></Layout></PrivateRoute>} />
          <Route path="/admin/analytics"     element={<PrivateRoute roles={["superadmin","executive"]}><Layout><AdminAnalytics /></Layout></PrivateRoute>} />
          <Route path="/admin/policy"        element={<PrivateRoute superadminOnly><Layout><PolicyRules /></Layout></PrivateRoute>} />
          <Route path="/admin/oet-costs"     element={<PrivateRoute superadminOnly><Layout><OETExamCosts /></Layout></PrivateRoute>} />
          <Route path="/admin/test-data"     element={<PrivateRoute superadminOnly><Layout><TestDataCleanup /></Layout></PrivateRoute>} />

          {/* Executive / Chairman */}
          <Route path="/executive" element={<PrivateRoute roles={["executive"]}><Layout><ExecutiveDashboard /></Layout></PrivateRoute>} />
          <Route path="/executive/chairman-review" element={<PrivateRoute roles={["executive"]}><Layout><ChairmanReview /></Layout></PrivateRoute>} />

          {/* Catch-all — if logged in go to their home, otherwise to login */}
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </HashRouter>
      </SessionGuard>
      </PermissionsInit>
    </AuthProvider>
  );
}

